// 12 halftone renderers implemented client-side on <canvas>.
// Each renderer receives an ImageData source (grayscale-luminance) and draws
// press-ready patterns onto a target canvas at 300 DPI equivalent.

export const HALFTONE_STYLES = [
  { id: "am-round", name: "AM Round", desc: "Classic AM screen with round dots" },
  { id: "square", name: "Square", desc: "Square dots for a crisp geometric look" },
  { id: "diamond", name: "Diamond", desc: "Diamond-shaped dots, softer transitions" },
  { id: "ellipse", name: "Ellipse", desc: "Elliptical dots — great for skin tones" },
  { id: "line-h", name: "Line H", desc: "Horizontal line halftone" },
  { id: "line-45", name: "Line 45°", desc: "Angled lines, classic newspaper" },
  { id: "cross", name: "Cross", desc: "Plus/cross dot pattern" },
  { id: "hex", name: "Hex", desc: "Hexagonal packed dots" },
  { id: "concentric", name: "Concentric", desc: "Rings that thicken with density" },
  { id: "wave", name: "Wave", desc: "Wavy lines that modulate with tone" },
  { id: "fm", name: "FM Stochastic", desc: "Random dot dispersion for organic feel" },
  { id: "newsprint", name: "Newsprint", desc: "Large-dot vintage newspaper style" },
];

export const DEFAULT_HALFTONE_PARAMS = {
  lpi: 30, // dots per inch — mapped to cell size
  angle: 22.5,
  dpi: 300,
  invert: false,
  channel: "luminance", // luminance | red | green | blue
  underbase: true,
  underbaseChoke: 1, // px
  fabric: "dark", // dark | light | color
  fgColor: "#000000",
  bgTransparent: true,
};

// Utility: draw source image onto working canvas at target size
export function drawSourceToCanvas(img, targetW, targetH) {
  const c = document.createElement("canvas");
  c.width = targetW;
  c.height = targetH;
  const ctx = c.getContext("2d");
  ctx.imageSmoothingQuality = "high";
  ctx.drawImage(img, 0, 0, targetW, targetH);
  return c;
}

function luminance(r, g, b) {
  return 0.2126 * r + 0.7152 * g + 0.0722 * b;
}

// Return greyscale ImageData sampled from source canvas
function toGreyscale(srcCanvas, channel = "luminance") {
  const w = srcCanvas.width, h = srcCanvas.height;
  const ctx = srcCanvas.getContext("2d");
  const data = ctx.getImageData(0, 0, w, h).data;
  const grey = new Float32Array(w * h);
  const alpha = new Uint8ClampedArray(w * h);
  for (let i = 0, j = 0; i < data.length; i += 4, j++) {
    let v;
    if (channel === "red") v = data[i];
    else if (channel === "green") v = data[i + 1];
    else if (channel === "blue") v = data[i + 2];
    else v = luminance(data[i], data[i + 1], data[i + 2]);
    grey[j] = v; // 0..255
    alpha[j] = data[i + 3];
  }
  return { grey, alpha, w, h };
}

// Sample average density inside a cell rectangle
function sampleCell(grey, alpha, w, h, cx, cy, cellSize) {
  const half = cellSize / 2;
  let x0 = Math.max(0, Math.floor(cx - half));
  let y0 = Math.max(0, Math.floor(cy - half));
  let x1 = Math.min(w, Math.ceil(cx + half));
  let y1 = Math.min(h, Math.ceil(cy + half));
  let sum = 0, count = 0, aSum = 0;
  for (let y = y0; y < y1; y++) {
    for (let x = x0; x < x1; x++) {
      const idx = y * w + x;
      sum += grey[idx];
      aSum += alpha[idx];
      count++;
    }
  }
  if (!count) return { density: 0, alpha: 0 };
  const avg = sum / count; // 0..255
  const avgA = aSum / count;
  return { density: 1 - avg / 255, alpha: avgA / 255 };
}

// Main entry: render halftone from a loaded HTMLImageElement to a target canvas
export function renderHalftone(img, targetCanvas, params = DEFAULT_HALFTONE_PARAMS, style = "am-round") {
  const p = { ...DEFAULT_HALFTONE_PARAMS, ...params };
  const dpi = p.dpi || 300;
  const lpi = p.lpi || 30;
  const angleRad = ((p.angle || 0) * Math.PI) / 180;
  const cellSize = Math.max(2, dpi / lpi); // px per cell

  // Work at intrinsic size or scaled up to 300dpi based on image dims
  const w = img.naturalWidth || img.width;
  const h = img.naturalHeight || img.height;
  const src = drawSourceToCanvas(img, w, h);
  const { grey, alpha } = toGreyscale(src, p.channel);

  // Setup output canvas
  targetCanvas.width = w;
  targetCanvas.height = h;
  const ctx = targetCanvas.getContext("2d");
  ctx.clearRect(0, 0, w, h);
  if (!p.bgTransparent) {
    ctx.fillStyle = "#ffffff";
    ctx.fillRect(0, 0, w, h);
  }
  ctx.fillStyle = p.fgColor || "#000000";
  ctx.strokeStyle = p.fgColor || "#000000";

  const cos = Math.cos(angleRad), sin = Math.sin(angleRad);
  const rot = (x, y) => ({ x: x * cos - y * sin, y: x * sin + y * cos });

  // Draw pattern by iterating cell centers along rotated grid
  const diag = Math.hypot(w, h);
  const half = diag; // extend beyond image to cover rotation
  const maxDot = cellSize * 0.9;

  function drawRoundDot(cx, cy, density) {
    const r = Math.sqrt(density) * maxDot * 0.5;
    if (r <= 0.25) return;
    ctx.beginPath();
    ctx.arc(cx, cy, r, 0, Math.PI * 2);
    ctx.fill();
  }

  function drawSquareDot(cx, cy, density) {
    const s = Math.sqrt(density) * maxDot;
    if (s <= 0.5) return;
    ctx.fillRect(cx - s / 2, cy - s / 2, s, s);
  }

  function drawDiamondDot(cx, cy, density) {
    const s = Math.sqrt(density) * maxDot * 0.7;
    if (s <= 0.5) return;
    ctx.beginPath();
    ctx.moveTo(cx, cy - s);
    ctx.lineTo(cx + s, cy);
    ctx.lineTo(cx, cy + s);
    ctx.lineTo(cx - s, cy);
    ctx.closePath();
    ctx.fill();
  }

  function drawEllipseDot(cx, cy, density) {
    const rx = Math.sqrt(density) * maxDot * 0.55;
    const ry = rx * 0.65;
    if (rx <= 0.5) return;
    ctx.save();
    ctx.translate(cx, cy);
    ctx.rotate(angleRad);
    ctx.beginPath();
    ctx.ellipse(0, 0, rx, ry, 0, 0, Math.PI * 2);
    ctx.fill();
    ctx.restore();
  }

  function drawCrossDot(cx, cy, density) {
    const s = Math.sqrt(density) * maxDot * 0.9;
    if (s <= 0.5) return;
    const t = Math.max(1, s * 0.35);
    ctx.fillRect(cx - s / 2, cy - t / 2, s, t);
    ctx.fillRect(cx - t / 2, cy - s / 2, t, s);
  }

  function drawConcentric(cx, cy, density) {
    if (density <= 0.02) return;
    const rings = Math.max(1, Math.round(density * 4));
    ctx.lineWidth = Math.max(0.5, cellSize * 0.08);
    for (let i = 1; i <= rings; i++) {
      const r = (i / rings) * (maxDot * 0.5);
      ctx.beginPath();
      ctx.arc(cx, cy, r, 0, Math.PI * 2);
      ctx.stroke();
    }
  }

  function drawHexDot(cx, cy, density) {
    const s = Math.sqrt(density) * maxDot * 0.55;
    if (s <= 0.5) return;
    ctx.beginPath();
    for (let i = 0; i < 6; i++) {
      const a = (Math.PI / 3) * i + Math.PI / 6;
      const px = cx + Math.cos(a) * s;
      const py = cy + Math.sin(a) * s;
      if (i === 0) ctx.moveTo(px, py);
      else ctx.lineTo(px, py);
    }
    ctx.closePath();
    ctx.fill();
  }

  // For line styles, iterate lines instead of dot grid
  function drawLines(orientationRad) {
    const step = cellSize;
    const cosO = Math.cos(orientationRad), sinO = Math.sin(orientationRad);
    ctx.save();
    for (let d = -half; d < half; d += step) {
      // Sample density along a line perpendicular projection
      // For each pixel along the line, look up density and modulate stroke thickness
      const path = new Path2D();
      const segs = Math.max(60, Math.floor(diag / 3));
      const start = -half;
      const end = half;
      const dx = (end - start) / segs;
      // Draw multiple short segments with varying width
      let prevX = null, prevY = null;
      for (let t = 0; t <= segs; t++) {
        const u = start + dx * t;
        // parametric point on line at offset d
        const lx = u * cosO - d * sinO + w / 2;
        const ly = u * sinO + d * cosO + h / 2;
        if (lx < 0 || ly < 0 || lx >= w || ly >= h) { prevX = null; continue; }
        const idx = (ly | 0) * w + (lx | 0);
        const dens = 1 - grey[idx] / 255;
        const thickness = Math.max(0, dens * step * 0.9);
        if (thickness < 0.6) { prevX = null; continue; }
        if (prevX !== null) {
          ctx.lineWidth = thickness;
          ctx.beginPath();
          ctx.moveTo(prevX, prevY);
          ctx.lineTo(lx, ly);
          ctx.stroke();
        }
        prevX = lx; prevY = ly;
      }
    }
    ctx.restore();
  }

  function drawWave(orientationRad) {
    const step = cellSize;
    const cosO = Math.cos(orientationRad), sinO = Math.sin(orientationRad);
    const amp = cellSize * 0.35;
    const freq = (Math.PI * 2) / (cellSize * 3);
    ctx.save();
    for (let d = -half; d < half; d += step) {
      let prevX = null, prevY = null;
      const segs = Math.max(80, Math.floor(diag / 2));
      const dx = (2 * half) / segs;
      for (let t = 0; t <= segs; t++) {
        const u = -half + dx * t;
        const wy = d + Math.sin(u * freq) * amp;
        const lx = u * cosO - wy * sinO + w / 2;
        const ly = u * sinO + wy * cosO + h / 2;
        if (lx < 0 || ly < 0 || lx >= w || ly >= h) { prevX = null; continue; }
        const idx = (ly | 0) * w + (lx | 0);
        const dens = 1 - grey[idx] / 255;
        const thickness = Math.max(0, dens * step * 0.6);
        if (thickness < 0.5) { prevX = null; continue; }
        if (prevX !== null) {
          ctx.lineWidth = thickness;
          ctx.beginPath();
          ctx.moveTo(prevX, prevY);
          ctx.lineTo(lx, ly);
          ctx.stroke();
        }
        prevX = lx; prevY = ly;
      }
    }
    ctx.restore();
  }

  function drawFM() {
    // Blue-noise-ish random threshold using ordered permutation of Bayer + jitter
    // Iterate every pixel at reduced resolution
    const step = Math.max(1, Math.floor(dpi / (lpi * 4)));
    const dotR = Math.max(0.8, step * 0.9);
    for (let y = 0; y < h; y += step) {
      for (let x = 0; x < w; x += step) {
        const idx = y * w + x;
        const dens = 1 - grey[idx] / 255;
        if (Math.random() < dens) {
          ctx.beginPath();
          ctx.arc(x + Math.random() * step, y + Math.random() * step, dotR / 2, 0, Math.PI * 2);
          ctx.fill();
        }
      }
    }
  }

  function iterateGrid(hexOffset, drawFn) {
    const step = cellSize;
    let row = 0;
    for (let uy = -half; uy < half; uy += step) {
      row++;
      const offsetX = hexOffset && row % 2 === 0 ? step / 2 : 0;
      for (let ux = -half; ux < half; ux += step) {
        const rx = (ux + offsetX) * cos - uy * sin + w / 2;
        const ry = (ux + offsetX) * sin + uy * cos + h / 2;
        if (rx < -step || ry < -step || rx > w + step || ry > h + step) continue;
        const { density, alpha: a } = sampleCell(grey, alpha, w, h, rx, ry, cellSize);
        if (a < 0.02) continue;
        const dens = p.invert ? 1 - density : density;
        drawFn(rx, ry, dens);
      }
    }
  }

  switch (style) {
    case "square":
      iterateGrid(false, drawSquareDot); break;
    case "diamond":
      iterateGrid(false, drawDiamondDot); break;
    case "ellipse":
      iterateGrid(false, drawEllipseDot); break;
    case "cross":
      iterateGrid(false, drawCrossDot); break;
    case "hex":
      iterateGrid(true, drawHexDot); break;
    case "concentric":
      iterateGrid(false, drawConcentric); break;
    case "line-h":
      drawLines(0); break;
    case "line-45":
      drawLines(Math.PI / 4); break;
    case "wave":
      drawWave(0); break;
    case "fm":
      drawFM(); break;
    case "newsprint":
      // larger, chunkier cells
      const bigCell = cellSize * 1.6;
      const origMaxDot = maxDot;
      // temporarily boost
      // eslint-disable-next-line no-inner-declarations
      function drawBig(cx, cy, d) {
        const r = Math.sqrt(d) * bigCell * 0.5;
        if (r < 0.5) return;
        ctx.beginPath();
        ctx.arc(cx, cy, r, 0, Math.PI * 2);
        ctx.fill();
      }
      iterateGrid(false, drawBig);
      void origMaxDot;
      break;
    case "am-round":
    default:
      iterateGrid(false, drawRoundDot);
  }

  return targetCanvas;
}

// Generate underbase from source alpha & density (simple choke)
export function renderUnderbase(img, targetCanvas, params = DEFAULT_HALFTONE_PARAMS) {
  const p = { ...DEFAULT_HALFTONE_PARAMS, ...params };
  const w = img.naturalWidth || img.width;
  const h = img.naturalHeight || img.height;
  const src = drawSourceToCanvas(img, w, h);
  const ctx = src.getContext("2d");
  const data = ctx.getImageData(0, 0, w, h);
  const px = data.data;
  for (let i = 0; i < px.length; i += 4) {
    const a = px[i + 3];
    const on = a > 40 ? 255 : 0;
    px[i] = on; px[i + 1] = on; px[i + 2] = on; px[i + 3] = on ? 255 : 0;
  }
  ctx.putImageData(data, 0, 0);

  targetCanvas.width = w;
  targetCanvas.height = h;
  const outCtx = targetCanvas.getContext("2d");
  outCtx.clearRect(0, 0, w, h);
  // Draw with slight choke: shrink by drawing on offset composite
  const choke = Math.max(0, p.underbaseChoke || 0);
  outCtx.filter = choke ? `blur(${choke}px)` : "none";
  outCtx.drawImage(src, 0, 0);
  outCtx.filter = "none";
  return targetCanvas;
}

export function canvasToBlob(canvas, type = "image/png", quality = 1) {
  return new Promise((res) => canvas.toBlob(res, type, quality));
}

export function downloadCanvas(canvas, filename = "halftone.png") {
  const link = document.createElement("a");
  link.download = filename;
  link.href = canvas.toDataURL("image/png");
  link.click();
}

export function loadImageFromFile(file) {
  return new Promise((res, rej) => {
    const fr = new FileReader();
    fr.onload = () => {
      const img = new Image();
      img.crossOrigin = "anonymous";
      img.onload = () => res(img);
      img.onerror = rej;
      img.src = fr.result;
    };
    fr.onerror = rej;
    fr.readAsDataURL(file);
  });
}

export function loadImageFromDataUrl(dataUrl) {
  return new Promise((res, rej) => {
    const img = new Image();
    img.crossOrigin = "anonymous";
    img.onload = () => res(img);
    img.onerror = rej;
    img.src = dataUrl;
  });
}

/**
 * Simulated Process separation: split an image into 4 CMYK channels and render
 * each as its own halftone at a different screen angle. Returns 4 canvases.
 */
export function renderCmykSeparation(img, params = DEFAULT_HALFTONE_PARAMS, style = "am-round") {
  const w = img.naturalWidth || img.width;
  const h = img.naturalHeight || img.height;
  const src = drawSourceToCanvas(img, w, h);
  const ctx = src.getContext("2d");
  const data = ctx.getImageData(0, 0, w, h).data;

  const angles = { C: 15, M: 75, Y: 0, K: 45 };
  const colors = { C: "#00b3ff", M: "#e6008b", Y: "#f5c845", K: "#0a0d15" };
  const channels = { C: null, M: null, Y: null, K: null };

  for (const ch of ["C", "M", "Y", "K"]) {
    const c = document.createElement("canvas");
    c.width = w; c.height = h;
    const cctx = c.getContext("2d");
    const id = cctx.createImageData(w, h);
    const dst = id.data;
    for (let i = 0, j = 0; i < data.length; i += 4, j += 4) {
      const rr = data[i] / 255, gg = data[i + 1] / 255, bb = data[i + 2] / 255;
      const aa = data[i + 3];
      const k = 1 - Math.max(rr, gg, bb);
      const denom = 1 - k || 1;
      const cc = (1 - rr - k) / denom;
      const mm = (1 - gg - k) / denom;
      const yy = (1 - bb - k) / denom;
      let value = 0;
      if (ch === "C") value = cc;
      else if (ch === "M") value = mm;
      else if (ch === "Y") value = yy;
      else value = k;
      const gray = 255 - Math.round(Math.max(0, Math.min(1, value)) * 255);
      dst[j] = gray; dst[j + 1] = gray; dst[j + 2] = gray; dst[j + 3] = aa;
    }
    cctx.putImageData(id, 0, 0);
    // Create image from this canvas and render halftone using the channel's angle+color
    const chImg = new Image();
    // Use synchronous data-url path so we can chain
    const dataUrl = c.toDataURL("image/png");
    const outCanvas = document.createElement("canvas");
    // We render synchronously by re-using the temp canvas as a synthetic image via drawImage
    const synth = document.createElement("canvas");
    synth.width = w; synth.height = h;
    synth.getContext("2d").drawImage(c, 0, 0);
    // synth acts as HTMLImageElement-like: renderHalftone reads naturalWidth/naturalHeight
    // but also works on drawImage-compatible sources.
    Object.defineProperty(synth, "naturalWidth", { value: w });
    Object.defineProperty(synth, "naturalHeight", { value: h });
    renderHalftone(synth, outCanvas, {
      ...params, angle: angles[ch], fgColor: colors[ch], bgTransparent: true,
    }, style);
    channels[ch] = outCanvas;
    void dataUrl; void chImg;
  }
  // Compose all four into a preview canvas
  const composite = document.createElement("canvas");
  composite.width = w; composite.height = h;
  const ccx = composite.getContext("2d");
  ccx.fillStyle = "#ffffff";
  ccx.fillRect(0, 0, w, h);
  ccx.globalCompositeOperation = "multiply";
  ccx.drawImage(channels.Y, 0, 0);
  ccx.drawImage(channels.M, 0, 0);
  ccx.drawImage(channels.C, 0, 0);
  ccx.drawImage(channels.K, 0, 0);
  ccx.globalCompositeOperation = "source-over";
  return { channels, composite, angles, colors };
}

/**
 * Mockup: composite a halftone canvas onto a t-shirt mockup image at a given
 * position/scale, with light blending to simulate ink absorption.
 */
export function renderMockup(mockupImg, artCanvas, target = { x: 0.5, y: 0.42, scale: 0.32 }) {
  const w = mockupImg.naturalWidth || mockupImg.width;
  const h = mockupImg.naturalHeight || mockupImg.height;
  const c = document.createElement("canvas");
  c.width = w; c.height = h;
  const ctx = c.getContext("2d");
  ctx.drawImage(mockupImg, 0, 0);
  const artW = Math.round(w * target.scale);
  const aspect = artCanvas.height / artCanvas.width;
  const artH = Math.round(artW * aspect);
  const cx = Math.round(w * target.x - artW / 2);
  const cy = Math.round(h * target.y - artH / 2);
  ctx.globalCompositeOperation = "multiply";
  ctx.globalAlpha = 0.94;
  ctx.drawImage(artCanvas, cx, cy, artW, artH);
  ctx.globalAlpha = 1;
  ctx.globalCompositeOperation = "source-over";
  return c;
}
